// Hooks for SMTP box management — pool health dashboard

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { BoxDetailResponse, BoxListResponse, PoolStatusResponse } from '@/lib/types-boxes'

const BOXES_REFETCH_MS = 15_000

export function useBoxes() {
  return useQuery<BoxListResponse>({
    queryKey: ['boxes'],
    queryFn: () => apiClient.get(endpoints.boxes.list),
    refetchInterval: BOXES_REFETCH_MS,
  })
}

export function useBoxDetail(id: string) {
  return useQuery<BoxDetailResponse>({
    queryKey: ['boxes', id],
    queryFn: () => apiClient.get(endpoints.boxes.detail(id)),
    enabled: !!id,
    refetchInterval: BOXES_REFETCH_MS,
  })
}

export function usePoolStatus() {
  return useQuery<PoolStatusResponse>({
    queryKey: ['boxes', 'pool-status'],
    queryFn: () => apiClient.get(endpoints.boxes.poolStatus),
    refetchInterval: BOXES_REFETCH_MS,
  })
}

export function useUpdateBox() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(endpoints.boxes.update(id), data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['boxes'] })
    },
  })
}

export function useUpdateBoxWeight() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, weight }: { id: string; weight: number }) =>
      apiClient.patch(endpoints.boxes.weight(id), { weight }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['boxes'] })
    },
  })
}

export function usePauseBox() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.post(endpoints.boxes.pause(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['boxes'] })
    },
  })
}

export function useResumeBox() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.post(endpoints.boxes.resume(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['boxes'] })
    },
  })
}
